import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, effect, inject, input, model, output } from '@angular/core';

declare let ace: any;

// The modes, theme and workers this component uses, copied from ace-builds into the build
// (angular.json assets) so they always match the ace.js loaded from the same package (scripts).
const ACE_BUILDS_URL = 'assets/js/ace-builds';
const ACE_MODULES = ['theme/monokai', 'mode/html', 'mode/javascript', 'mode/css'];
const ACE_WORKERS = ['html', 'javascript', 'css'];
let aceModulesConfigured = false;

const configureAceModules = (): void => {
  if (aceModulesConfigured) {
    return;
  }
  aceModulesConfigured = true;
  for (const module of ACE_MODULES) {
    ace.config.setModuleUrl(`ace/${module}`, `${ACE_BUILDS_URL}/${module.replace('/', '-')}.js`);
  }
  for (const worker of ACE_WORKERS) {
    ace.config.setModuleUrl(`ace/mode/${worker}_worker`, `${ACE_BUILDS_URL}/worker-${worker}.js`);
  }
};

/**
 * A code editor on the ace-builds script the app loads globally, with the inputs and outputs the
 * panel used from ng2-ace-editor: `[(text)]` (or `[text]` with `(textChanged)`), `mode`, `theme`
 * (monokai), `options` (ace's setOptions), `readOnly`, and `autoUpdateContent` (a new `text` replaces
 * the editor's content; off, the editor keeps what is typed in it). `getEditor()` returns ace's
 * editor.
 */
@Component({
  selector: 'app-ace-editor',
  template: '',
  styles: [':host { display: block; width: 100%; }'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AceEditorComponent implements OnInit, OnDestroy {
  private element = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly textChanged = output<string>();
  /** The editor's text; an edit sets it, so `[(text)]` hears of it (as `(textChanged)` does). */
  readonly text = model<string | null | undefined>('');
  readonly autoUpdateContent = input(true);
  readonly mode = input('html');
  readonly theme = input('monokai');
  /** Ace's setOptions. */
  readonly options = input<any>({});
  readonly readOnly = input(false);

  private editor: any;
  private currentText = '';

  constructor() {
    // A bound value reaches the editor once it exists; ngOnInit gives it the first ones.
    effect(() => this.setText(this.text()));
    effect(() => this.editor?.getSession().setMode(`ace/mode/${this.mode()}`));
    effect(() => this.editor?.setTheme(`ace/theme/${this.theme()}`));
    effect(() => this.editor?.setOptions(this.options() || {}));
    effect(() => this.editor?.setReadOnly(!!this.readOnly()));
  }

  ngOnInit(): void {
    configureAceModules();
    this.setText(this.text());
    this.editor = ace.edit(this.element.nativeElement);
    this.editor.$blockScrolling = Infinity;
    this.editor.setOptions(this.options() || {});
    this.editor.setTheme(`ace/theme/${this.theme()}`);
    this.editor.getSession().setMode(`ace/mode/${this.mode()}`);
    this.editor.setReadOnly(!!this.readOnly());
    this.editor.setValue(this.currentText);
    this.editor.clearSelection();
    this.editor.on('change', () => this.onEdited());
    this.editor.on('paste', () => this.onEdited());
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  getEditor(): any {
    return this.editor;
  }

  private setText(text: string | null | undefined): void {
    const value = text ?? '';
    if (value === this.currentText || !this.autoUpdateContent()) {
      return;
    }
    this.currentText = value;
    if (this.editor) {
      this.editor.setValue(value);
      this.editor.clearSelection();
    }
  }

  private onEdited(): void {
    const value = this.editor.getValue();
    if (value === this.currentText) {
      return;
    }
    this.currentText = value;
    this.text.set(value);
    this.textChanged.emit(value);
  }
}
